import { useState, useCallback, useMemo, useRef } from 'react';
import { useQuery, useLazyQuery } from '@apollo/client';
import { Tabs, TabsList, TabsTrigger, Slider } from '@artstorefronts/ui';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

import AdminLayout from '../../components/AdminLayout';
import GenomeGraph, { type GenomeGraphHandle } from '../../components/admin/GenomeGraph';
import GenomeMap from '../../components/admin/GenomeMap';
import GenomeDetailPanel from '../../components/admin/GenomeDetailPanel';
import ArtistSearch from '../../components/admin/ArtistSearch';
import { GET_PCA, GET_NEIGHBORS, GET_COMPARE } from '../../graphql/admin-artist-genome-queries';

type GenomeAxis = 'VISUAL' | 'IDENTITY' | 'COMBINED';

const MAX_PINNED = 8;

function ArtistGenome() {
  const { t } = useTranslation('admin');
  const [axis, setAxis] = useState<GenomeAxis>('VISUAL');
  const [connectionLimit, setConnectionLimit] = useState(10);
  const [viewMode, setViewMode] = useState<'graph' | 'map'>('graph');
  const [anchorUserId, setAnchorUserId] = useState<string | null>(null);
  const [pinnedUserIds, setPinnedUserIds] = useState<string[]>([]);
  const [compareUserId, setCompareUserId] = useState<string | null>(null);
  const graphRef = useRef<GenomeGraphHandle>(null);

  const { data: pcaData, loading: pcaLoading } = useQuery(GET_PCA, {
    variables: { axis },
  });

  const [fetchNeighbors, { data: neighborData, loading: neighborsLoading }] = useLazyQuery(GET_NEIGHBORS);

  const [fetchCompare, { data: compareResult, loading: compareLoading }] = useLazyQuery(GET_COMPARE);

  const handleSelectArtist = useCallback(
    (userId: string, options: { shiftKey: boolean } = { shiftKey: false }) => {
      if (options.shiftKey) {
        setCompareUserId(null);
        if (userId === anchorUserId) {
          if (pinnedUserIds.length === 0) {
            setAnchorUserId(null);
            return;
          }
          const [newAnchor, ...rest] = pinnedUserIds;
          if (!newAnchor) {
            setAnchorUserId(null);
            setPinnedUserIds([]);
            return;
          }
          setAnchorUserId(newAnchor);
          setPinnedUserIds(rest);
          fetchNeighbors({ variables: { userId: newAnchor, axis, limit: connectionLimit } });
          return;
        }
        if (pinnedUserIds.includes(userId)) {
          setPinnedUserIds(pinnedUserIds.filter((id) => id !== userId));
          return;
        }
        if (!anchorUserId) {
          setAnchorUserId(userId);
          fetchNeighbors({ variables: { userId, axis, limit: connectionLimit } });
          graphRef.current?.centerOnArtist(userId);
          return;
        }
        if (pinnedUserIds.length >= MAX_PINNED) return;
        setPinnedUserIds([...pinnedUserIds, userId]);
        return;
      }
      setAnchorUserId(userId);
      setPinnedUserIds([]);
      setCompareUserId(null);
      fetchNeighbors({ variables: { userId, axis, limit: connectionLimit } });
      graphRef.current?.centerOnArtist(userId);
    },
    [anchorUserId, pinnedUserIds, axis, connectionLimit, fetchNeighbors]
  );

  const handleAxisChange = useCallback((newAxis: string) => {
    setAxis(newAxis as GenomeAxis);
    setAnchorUserId(null);
    setPinnedUserIds([]);
    setCompareUserId(null);
  }, []);

  const handleConnectionLimitChange = useCallback((values: number[]) => {
    const newLimit = values[0];
    if (newLimit == null) return;
    setConnectionLimit(newLimit);
  }, []);

  const handleConnectionLimitCommit = useCallback(
    (values: number[]) => {
      const newLimit = values[0];
      if (newLimit == null) return;
      if (anchorUserId) {
        fetchNeighbors({ variables: { userId: anchorUserId, axis, limit: newLimit } });
      }
    },
    [anchorUserId, axis, fetchNeighbors]
  );

  const handleCompare = useCallback(
    (userId: string) => {
      if (!anchorUserId) return;
      setCompareUserId(userId);
      fetchCompare({ variables: { userIdA: anchorUserId, userIdB: userId } });
    },
    [anchorUserId, fetchCompare]
  );

  const handleCloseCompare = useCallback(() => {
    setCompareUserId(null);
  }, []);

  const handleUnpin = useCallback((userId: string) => {
    setPinnedUserIds((prev) => prev.filter((id) => id !== userId));
  }, []);

  const handleClearAnchor = useCallback(() => {
    setAnchorUserId(null);
    setPinnedUserIds([]);
    setCompareUserId(null);
  }, []);

  const points = pcaData?.adminArtistGenomePCA ?? [];
  const neighbors = anchorUserId ? (neighborData?.adminArtistGenomeNeighbors ?? []) : [];
  const compareData = useMemo(() => {
    if (!compareUserId || !anchorUserId) return null;
    const result = compareResult?.adminArtistGenomeCompare;
    if (!result) return null;
    if (result.artistA?.userId !== anchorUserId || result.artistB?.userId !== compareUserId) return null;
    return result;
  }, [compareUserId, anchorUserId, compareResult]);

  const selectedArtistGeo = useMemo(() => {
    if (!anchorUserId) return null;
    const point = points.find(
      (p: { userId: string; latitude?: number; longitude?: number }) => p.userId === anchorUserId
    );
    return {
      userId: anchorUserId,
      latitude: (point?.latitude as number | null) ?? null,
      longitude: (point?.longitude as number | null) ?? null,
    };
  }, [anchorUserId, points]);

  return (
    <AdminLayout title={t('artist-genome.page-title')} description={t('artist-genome.page-description')}>
      <div className="flex items-center gap-4 pb-4">
        <Tabs value={axis} onValueChange={handleAxisChange}>
          <TabsList>
            {(['VISUAL', 'IDENTITY', 'COMBINED'] as const).map((a) => (
              <TabsTrigger key={a} value={a} className="text-xs">
                {t(`artist-genome.axis-${a.toLowerCase()}`)}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-zinc-500">{t('artist-genome.connections-label')}</span>
          <Slider
            value={[connectionLimit]}
            onValueChange={handleConnectionLimitChange}
            onValueCommit={handleConnectionLimitCommit}
            min={3}
            max={25}
            step={1}
            className="w-32"
          />
          <span className="w-6 text-center text-xs font-medium text-zinc-900">{connectionLimit}</span>
        </div>

        <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as 'graph' | 'map')}>
          <TabsList>
            <TabsTrigger value="graph" className="text-xs">
              {t('artist-genome.view-graph')}
            </TabsTrigger>
            <TabsTrigger value="map" className="text-xs">
              {t('artist-genome.view-map')}
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <ArtistSearch points={points} selectedUserId={anchorUserId} onSelect={(id) => handleSelectArtist(id)} />

        {(pcaLoading || neighborsLoading) && <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />}
      </div>

      <div className="-mx-8 flex" style={{ height: 'calc(100dvh - 300px)' }}>
        <div className="min-w-0 flex-1">
          {viewMode === 'graph' ? (
            points.length === 0 && !pcaLoading ? (
              <div className="flex h-full items-center justify-center text-sm text-zinc-400">
                {t('artist-genome.no-centroids')}
              </div>
            ) : (
              <GenomeGraph
                ref={graphRef}
                points={points}
                neighbors={neighbors}
                anchorUserId={anchorUserId}
                pinnedUserIds={pinnedUserIds}
                axis={axis}
                onSelectArtist={handleSelectArtist}
              />
            )
          ) : (
            <GenomeMap
              selectedArtist={selectedArtistGeo}
              neighbors={neighbors}
              onArtistClick={(id) => handleSelectArtist(id)}
            />
          )}
        </div>

        <GenomeDetailPanel
          points={points}
          neighbors={neighbors}
          anchorUserId={anchorUserId}
          pinnedUserIds={pinnedUserIds}
          axis={axis}
          viewMode={viewMode}
          onSelectArtist={(id) => handleSelectArtist(id)}
          onCompare={handleCompare}
          compareData={compareData}
          compareLoading={compareLoading && compareUserId != null && !compareData}
          onCloseCompare={handleCloseCompare}
          onUnpinArtist={handleUnpin}
          onClearAnchor={handleClearAnchor}
        />
      </div>
    </AdminLayout>
  );
}

export default ArtistGenome;
